import React from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FiMenu } from 'react-icons/fi';
import { useMediaQuery } from 'react-responsive';
import { FaCircleQuestion } from 'react-icons/fa6';
import { ReactComponent as SearchIcon } from '../../assets/icons/search_icon_header.svg';
import { isObjectNotEmpty, isStringNotEmpty, isListNotEmpty, isBooleanTrue, isBooleanFalse } from '../../utils/objectUtils';
import { useAuth } from '../../redux/ducks/auth';
import { UserAvatarMenu } from './UserAvatarMenu';
import { MessagesMenu } from './MessagesMenu';
import { NotificationsMenu } from './NotificationsMenu';
import styles from './DashboardPageHeader.module.css';

/**
 * Returns the time-of-day greeting used when a page does not provide a title.
 *
 * @returns {string} Greeting for the current local hour.
 */
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

/**
 * Renders the shared V2 dashboard header with mobile menu access, title/greeting,
 * optional search, search help, messages, notifications, and profile menu.
 *
 * @param {object} props
 * @param {() => void} [props.onMenuToggle] - Opens the mobile account navigation.
 * @param {string} [props.displayTitle=''] - Explicit page title shown instead of greeting.
 * @param {boolean} [props.disableBleed=false] - Removes the header bleed styling.
 * @param {string} [props.searchValue=''] - Controlled search input value.
 * @param {(value: string) => void} [props.onSearchChange] - Search input change handler.
 * @param {string} [props.searchPlaceholder='Search'] - Search input placeholder.
 * @param {string} [props.searchAriaLabel='Search dashboard'] - Accessible search label.
 * @param {{title?: string, items?: string[]}|null} [props.searchHelp=null] - Optional search help tooltip content.
 * @param {boolean} [props.disableSearch=false] - Hides search when true.
 * @param {string} [props.headerClassName=''] - Additional header class name; must not set width or horizontal margin, which break the full-bleed.
 * @returns {JSX.Element} Dashboard page header.
 */
export const DashboardPageHeader = ({
  onMenuToggle,
  displayTitle = '',
  disableBleed = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  searchAriaLabel = 'Search dashboard',
  searchHelp = null,
  disableSearch = false,
  headerClassName: externalHeaderClassName = ''
}) => {
  const { firstName } = useAuth();
  const greeting = getGreeting();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const hasDisplayTitle = isStringNotEmpty(displayTitle.trim());
  const headerTitle = isBooleanTrue(hasDisplayTitle) ? displayTitle : isBooleanTrue(isMobile) ? `Hi ${firstName} 👋` : `${greeting}, ${firstName} 👋`;
  const baseHeaderClassName = isBooleanTrue(disableBleed)
    ? `${styles.dashboardPageHeader} ${styles.dashboardPageHeaderNoBleed}`
    : styles.dashboardPageHeader;

  const mergedHeaderClassName = [baseHeaderClassName, externalHeaderClassName]
    .filter(isStringNotEmpty)
    .join(' ');

  const handleSearchChange = (event) => {
    if (typeof onSearchChange === 'function') {
      onSearchChange(event.target.value);
    }
  };

  const showSearchHelp = isObjectNotEmpty(searchHelp)
    && (isStringNotEmpty(searchHelp?.title) || Array.isArray(searchHelp?.items));
  const shouldShowSearch = !disableSearch;

  const renderSearchHelpTooltip = (isMobile = false) => {
    if (isBooleanFalse(showSearchHelp)) return null;

    return (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        placement={isBooleanTrue(isMobile) ? 'bottom' : 'bottom'}
        rootClose
        overlay={(
          <Tooltip id={`dashboard-search-help-${isBooleanTrue(isMobile) ? 'mobile' : 'desktop'}`} className={styles.dashboardPageHeaderSearchHelpTooltip}>
            {isStringNotEmpty(searchHelp?.title) ? <div className={styles.dashboardPageHeaderSearchHelpTitle}>{searchHelp.title}</div> : null}
            {isListNotEmpty(searchHelp?.items) ? (
              <ul className={styles.dashboardPageHeaderSearchHelpList}>
                {searchHelp.items.map((item) => (
                  <li key={item} className={styles.dashboardPageHeaderSearchHelpItem}>{item}</li>
                ))}
              </ul>
            ) : null}
          </Tooltip>
        )}
      >
        <button
          type="button"
          className={`${styles.dashboardPageHeaderSearchHelpButton} ${isBooleanTrue(isMobile) ? styles.dashboardPageHeaderSearchHelpButtonMobile : ''}`}
          aria-label="Search help"
        >
          <FaCircleQuestion aria-hidden="true" />
        </button>
      </OverlayTrigger>
    );
  };


  return (
    <header className={mergedHeaderClassName}>
      <div className={styles.dashboardPageHeaderTopRow}>
        <button
          type="button"
          className={styles.dashboardPageHeaderMenuButton}
          onClick={onMenuToggle}
          aria-label="Open menu"
          data-tour="menu-button"
        >
          <FiMenu />
        </button>

        <h1 className={styles.dashboardPageHeaderGreeting}>
          {headerTitle} 
        </h1>

        {isBooleanTrue(shouldShowSearch) && (
          <div className={styles.dashboardPageHeaderSearch}>
            <Form.Control
              type="search"
              className={styles.dashboardPageHeaderSearchInput}
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel}
              value={searchValue}
              onChange={handleSearchChange}
            />
            <SearchIcon className={styles.dashboardPageHeaderSearchIcon} aria-hidden="true" />
            {renderSearchHelpTooltip()}
          </div>
        )}

        <div className={styles.dashboardPageHeaderActions}>
          <MessagesMenu />
          <NotificationsMenu />
          <UserAvatarMenu />
        </div>
      </div>

      {isBooleanTrue(shouldShowSearch) && (
        <div className={styles.dashboardPageHeaderSearchMobileRow}>
          <div className={styles.dashboardPageHeaderSearch}>
            <Form.Control
              type="search"
              className={styles.dashboardPageHeaderSearchInput}
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel}
              value={searchValue}
              onChange={handleSearchChange}
            />
            <SearchIcon className={styles.dashboardPageHeaderSearchIcon} aria-hidden="true" />
            {renderSearchHelpTooltip(true)}
          </div>
        </div>
      )}
    </header>
  );
};
